import { Button } from "@/src/components/ui/button";
import { Content } from "@/src/components/wrappers/Content";
import { Link, Outlet } from "react-router-dom";
import { ActionsContext, useEmit } from "../../lib/actions";
import { Checkbox } from "../../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import { FiltersForm } from "./FiltersForm/FiltersForm";
import { ActionButton } from "../../components/wrappers/WithActionBar";
import { useEffect, useState } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { useBookingsStore } from "../../stores/bookingsStore";
import { useUserStore } from "@/src/stores/userStore";

// The logged-in id is a HubSpot owner id; offers/bookings reference the database
// user id, so match on that (falling back keeps pre-fix sessions no worse off).
function brokerDbId(u: any): number | undefined {
  const v = u?.databaseId ?? u?.id;
  return v == null || Number.isNaN(+v) ? undefined : +v;
}

export function Bookings() {
  const [visible, setVisible] = useState(false);
  const { filters } = useBookingsStore();

  return (
    <ActionsContext>
      <Content title="Bookings">
        <div className="mb-2 flex flex-col gap-2">
          <div className="flex justify-between">
            <div className="flex gap-4">
              <Dialog onOpenChange={(v) => setVisible(v)} open={visible}>
                <DialogTrigger asChild>
                  <Button className="bg-white" variant="outline">
                    Filters
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-[720px] max-h-[calc(100%-80px)] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="text-3xl">Filters</DialogTitle>
                  </DialogHeader>
                  <FiltersForm onSave={() => setVisible(false)} />
                  <DialogFooter>
                    <ActionButton
                      disabled={(!!filters && !Object.keys(filters)?.length) || !filters}
                      variant="link"
                      className="mr-auto"
                      eventKey="filters:clear"
                    >
                      Clear all
                    </ActionButton>
                    <ActionButton eventKey="filters:save" type="submit">
                      Show bookings
                    </ActionButton>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
            <div className="flex gap-x-4">
              <MyBookingsCheckbox />
              <PageSizeSelect />
              <SortingSelect />
            </div>
          </div>
          <Outlet />
        </div>
      </Content>
    </ActionsContext>
  );
}

function MyBookingsCheckbox() {
  const { filters, setFilters } = useBookingsStore();
  const { user } = useUserStore();
  const [checked, setChecked] = useState(filters?.["broker"]?.id === brokerDbId(user));

  useEffect(() => {
    setChecked(filters?.["broker"]?.id === brokerDbId(user));
  }, [filters]);

  const handleClick = () => {
    const truncatedFilters = { ...filters };
    delete truncatedFilters["broker"];
    setChecked(!checked);
    return !checked
      ? setFilters({
          ...filters,
          broker: {
            id: brokerDbId(user)!,
            firstName: user?.firstName!,
            lastName: user?.lastName!,
            fullName: user?.firstName! + " " + user?.lastName!,
          },
        })
      : setFilters(truncatedFilters);
  };

  return (
    <div
      onClick={() => handleClick()}
      className="bg-white flex items-center gap-x-2 justify-center border border-slate-200 rounded-md px-4 cursor-pointer"
    >
      <Checkbox className="w-4 h-4" checked={checked} />
      <span>Show only my bookings</span>
    </div>
  );
}

function PageSizeSelect() {
  const [value, setValue] = useState("10");
  const emit = useEmit<number>("bkg:page-size");
  const pageChangeEmit = useEmit<number>("bkg:page");

  const onValueChange = (value: string) => {
    pageChangeEmit(1);
    setValue(value);
    emit(parseInt(value));
  };

  return (
    <div className="w-60">
      <Select onValueChange={onValueChange} value={value}>
        <SelectTrigger>
          <SelectValue placeholder="Page size" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="5">Show: 5 at once</SelectItem>
          <SelectItem value="10">Show: 10 at once</SelectItem>
          <SelectItem value="20">Show: 20 at once</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
}

function SortingSelect() {
  const [value, setValue] = useState("DESC");
  const emit = useEmit<string>("bkg:sorting");

  const onValueChange = (value: string) => {
    setValue(value);
    emit(value);
  };

  return (
    <div className="w-60">
      <Select onValueChange={onValueChange} value={value}>
        <SelectTrigger>
          <SelectValue placeholder="Page size" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="DESC">Last modified</SelectItem>
          <SelectItem value="ASC">First modified</SelectItem>
          <SelectItem value="start_date">Start date (closest)</SelectItem>
          <SelectItem value="booking_date">Booking date (oldest)</SelectItem>
          {/* @TBD*/}
          {/* <SelectItem value="nameASC">Status</SelectItem> */}
        </SelectContent>
      </Select>
    </div>
  );
}
